import type { CSSObject } from '@emotion/react'
import { useRef, useEffect, useState } from 'react'
import { Size, Spacing } from '@opallabs/gem'
import { Button, ButtonIconPosition, Icon, Tooltip } from '@opallabs/lapidary'
import type { WithStyleOverrides, WithTheme } from '@opallabs/lapidary'

type AddButtonProps = Readonly<
  WithStyleOverrides &
    WithTheme & {
      disabled?: boolean
      onBlur?: () => void
      onClickAdd: () => void
      tooltipMessage?: string | null
    }
>

const buttonStyles: CSSObject = {
  blockSize: Spacing.L,
  inlineSize: '100%',
}

const MIN_BUTTON_WIDTH_TEXT_DISPLAY = 70

export const CreateButton: React.FC<AddButtonProps> = ({
  disabled,
  onBlur,
  onClickAdd,
  styleOverrides = {},
  theme = 'light',
  tooltipMessage = null,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [hideButtonText, setHideButtonText] = useState(false)
  useEffect(() => {
    if (
      buttonRef.current?.clientWidth &&
      buttonRef.current?.clientWidth < MIN_BUTTON_WIDTH_TEXT_DISPLAY
    ) {
      setHideButtonText(true)
    } else {
      setHideButtonText(false)
    }
  }, [buttonRef, setHideButtonText])

  const displayProps = hideButtonText
    ? { ariaLabel: 'Create a Moment' }
    : { buttonText: 'Create', iconPosition: ButtonIconPosition.leading }

  return (
    <Tooltip
      disabled={!tooltipMessage}
      content={tooltipMessage ?? ''}
      side="top"
      zIndex={10000}
    >
      <div
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        onBlur={onBlur}
        onFocus={() => buttonRef.current?.focus()}
      >
        <Button
          {...displayProps}
          variant="secondary"
          className="add-moment-button"
          disabled={disabled}
          hideButtonText={hideButtonText}
          icon={
            <Icon
              ariaHidden
              glyph="gem-add"
              height={Size.Icon}
              width={Size.Icon}
            />
          }
          name="Create Moment"
          onClick={onClickAdd}
          overrideStyles={{
            ...buttonStyles,
            ...(styleOverrides as CSSObject),
          }}
          ref={buttonRef}
          {...{
            theme,
          }}
        />
      </div>
    </Tooltip>
  )
}
CreateButton.displayName = 'Continuum: CreateButton ← WeekView'
